/**
 * "Nice" ticks: round, evenly spaced axis values (1, 2, 5 × 10ⁿ) around a data extent, and the nice domain that
 * contains it.
 */
import { extent, isFiniteNumber } from './numbers.js';
/** The nice step (1, 2, 5, 10 × power of ten) that fits `count` intervals into `span`. */
export function niceStep(span, count) {
    if (!isFiniteNumber(span) || span <= 0 || count <= 0)
        return 1;
    const raw = span / count;
    const exp = Math.floor(Math.log10(raw));
    const base = 10 ** exp;
    const f = raw / base;
    // The usual thresholds (√2, √10, √50): pick the nice step closest to the raw one on a log scale.
    const nice = f >= Math.sqrt(50) ? 10 : f >= Math.sqrt(10) ? 5 : f >= Math.sqrt(2) ? 2 : 1;
    return nice * base;
}
/** Clean up float noise so 0.1 + 0.2 prints as 0.3 (ticks are compared and rendered). */
function tidy(value, step) {
    const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
    return Number(value.toFixed(Math.min(decimals, 12)));
}
/**
 * Nice ticks covering [min, max] with about `count` intervals. `min === max` (a flat series) widens the domain around the
 * value; a non-finite or reversed input is repaired rather than thrown on.
 */
export function niceTicks(min, max, count = 4) {
    let lo = isFiniteNumber(min) ? min : 0;
    let hi = isFiniteNumber(max) ? max : lo + 1;
    if (lo > hi)
        [lo, hi] = [hi, lo];
    if (lo === hi) {
        if (lo === 0)
            hi = 1;
        else {
            const pad = Math.abs(lo) * 0.5;
            lo -= pad;
            hi += pad;
        }
    }
    const step = niceStep(hi - lo, Math.max(1, count));
    // A small epsilon so 0.3 / 0.1 (= 3.0000000000000004) does not add a whole extra tick.
    const first = Math.floor(lo / step + 1e-9) * step;
    const last = Math.ceil(hi / step - 1e-9) * step;
    const ticks = [];
    const n = Math.round((last - first) / step);
    for (let i = 0; i <= n; i += 1)
        ticks.push(tidy(first + i * step, step));
    return { ticks, domain: [ticks[0] ?? 0, ticks[ticks.length - 1] ?? 1], step };
}
/**
 * The y domain and ticks for a set of values. No drawable values at all yields the stand-in domain [0, 1] so an empty
 * chart still has an axis to draw (the caller shows an empty state on top).
 */
export function valueDomain(values, options = {}) {
    const e = extent(values);
    const includeZero = options.includeZero ?? true;
    const count = options.tickCount ?? 4;
    if (e === null)
        return niceTicks(0, 1, count);
    const min = includeZero ? Math.min(0, e.min) : e.min;
    const max = includeZero ? Math.max(0, e.max) : e.max;
    if (options.nice === false) {
        const [lo, hi] = min === max ? [min - 1, max + 1] : [min, max];
        const step = (hi - lo) / count;
        return { ticks: Array.from({ length: count + 1 }, (_, i) => lo + i * step), domain: [lo, hi], step };
    }
    return niceTicks(min, max, count);
}
//# sourceMappingURL=ticks.js.map